/**
 * IntegrationsSettings — connect/disconnect third-party integrations.
 *
 * Shows one card per supported provider. OAuth providers (Slack, Google
 * Calendar, Outlook Calendar) surface a "Connect" button that kicks off
 * the backend's `/connect` endpoint and redirects to the provider's
 * authorize URL. Webhook-only providers (Payroll, Background Check)
 * show a webhook URL + signing-secret workflow instead.
 *
 * SUPER_ADMIN-only: the containing <SettingsPage> already gates the
 * Integrations tab, but we double-check here and render a friendly
 * message for non-admins in case the tab is surfaced elsewhere.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  MessageSquare,
  RefreshCw,
  Shield,
  Trash2,
  Wallet,
  Webhook,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  connectIntegration,
  disconnectIntegration,
  listIntegrationEvents,
  listIntegrations,
  listProviders,
  revealWebhookSecret,
  rotateWebhookSecret,
  updateIntegration,
  type IntegrationEvent,
  type IntegrationProvider,
  type IntegrationProviderInfo,
  type IntegrationRead,
} from "@/api/integrations";
import { TelegramBotCard } from "@/components/integrations/TelegramBotCard";
import { SlackCard } from "@/components/integrations/SlackCard";
import { TeamsCard } from "@/components/integrations/TeamsCard";
import { QuickBooksCard } from "@/components/integrations/QuickBooksCard";
import { CareersEmbedCard } from "@/components/integrations/CareersEmbedCard";
import { TwilioSmsCard } from "@/components/integrations/TwilioSmsCard";

const PROVIDER_ICON: Record<IntegrationProvider, LucideIcon> = {
  SLACK: MessageSquare,
  GOOGLE_CALENDAR: CalendarDays,
  OUTLOOK_CALENDAR: CalendarDays,
  PAYROLL: Wallet,
  BACKGROUND_CHECK: Shield,
};

const PROVIDER_BLURB: Record<IntegrationProvider, string> = {
  SLACK:
    "Post leave approvals, onboarding updates, and org-wide announcements into a Slack channel of your choice.",
  GOOGLE_CALENDAR:
    "Create calendar events automatically when time-off is approved — visible to the whole team.",
  OUTLOOK_CALENDAR:
    "Same as Google Calendar but for Outlook / Microsoft 365 users.",
  PAYROLL:
    "Receive webhook events from your payroll provider (Gusto, Rippling, ADP…). Sync runs, paystubs, and employment status.",
  BACKGROUND_CHECK:
    "Listen for candidate report updates from Checkr or a Checkr-shaped provider.",
};

export function IntegrationsSettings() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [providers, setProviders] = useState<IntegrationProviderInfo[]>([]);
  const [configs, setConfigs] = useState<IntegrationRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<IntegrationProvider | null>(null);
  const [secretOpen, setSecretOpen] = useState<IntegrationRead | null>(null);
  const [eventsOpen, setEventsOpen] = useState<IntegrationRead | null>(null);

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([listProviders(), listIntegrations()]);
      setProviders(p);
      setConfigs(c);
    } catch {
      addToast("error", "Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  // OAuth callback sometimes lands us back on /settings/integrations with
  // a ?connected=<provider> query param — surface a toast + refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected) {
      addToast("success", `${connected.replace("_", " ")} connected`);
    }
    if (error) {
      addToast("error", `Integration error: ${error}`);
    }
    if (connected || error) {
      const url = new URL(window.location.href);
      url.searchParams.delete("connected");
      url.searchParams.delete("error");
      url.searchParams.delete("account");
      window.history.replaceState({}, "", url.toString());
    }
  }, [addToast]);

  const byProvider = useMemo(() => {
    const map = new Map<IntegrationProvider, IntegrationRead>();
    configs.forEach((c) => map.set(c.provider, c));
    return map;
  }, [configs]);

  const handleConnect = async (provider: IntegrationProvider) => {
    setBusy(provider);
    try {
      const res = await connectIntegration(provider);
      // Off to the provider's consent screen.
      window.location.href = res.authorize_url;
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
      addToast("error", detail || "Could not start the OAuth flow");
      setBusy(null);
    }
  };

  const handleDisconnect = async (provider: IntegrationProvider) => {
    if (
      !window.confirm(
        `Disconnect ${provider.replace("_", " ")}? Lenavio will stop sending events.`,
      )
    ) {
      return;
    }
    setBusy(provider);
    try {
      await disconnectIntegration(provider);
      addToast("success", "Disconnected");
      await load();
    } catch {
      addToast("error", "Failed to disconnect");
    } finally {
      setBusy(null);
    }
  };

  const handleRotate = async (provider: IntegrationProvider) => {
    setBusy(provider);
    try {
      await rotateWebhookSecret(provider);
      addToast("success", "Webhook secret rotated");
      await load();
    } catch {
      addToast("error", "Could not rotate secret");
    } finally {
      setBusy(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Card>
        <div className="flex items-center gap-3 text-text-muted">
          <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
          <p className="text-sm">
            Only Super Admins can manage integrations. Ask an admin on your
            team to connect Slack, calendar, payroll, or background-check
            providers.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Integrations</CardTitle>
          {loading && (
            <Loader2
              className="h-4 w-4 animate-spin text-text-muted"
              aria-hidden="true"
            />
          )}
        </CardHeader>
        <p className="text-sm text-text-muted -mt-2">
          Connect Lenavio to the tools your team already uses. Changes take
          effect immediately; disconnecting a provider stops outbound events
          but keeps the event history for audit.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {providers.map((p) => {
          const cfg = byProvider.get(p.provider);
          const Icon = PROVIDER_ICON[p.provider];
          const status = cfg?.status ?? "DISCONNECTED";
          return (
            <Card key={p.provider} className="flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-accent/10 p-2">
                    <Icon
                      className="h-5 w-5 text-accent"
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold font-heading text-text-primary">
                      {p.label}
                    </h3>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">
                      {p.category}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={
                    status === "CONNECTED"
                      ? "success"
                      : status === "ERROR"
                        ? "danger"
                        : "default"
                  }
                >
                  {status === "CONNECTED"
                    ? "Connected"
                    : status === "ERROR"
                      ? "Error"
                      : "Not connected"}
                </Badge>
              </div>

              <p className="text-sm text-text-muted mb-4">
                {PROVIDER_BLURB[p.provider]}
              </p>

              {cfg?.account_label && (
                <p className="text-xs text-text-muted mb-2">
                  <span className="text-text-primary">Account:</span>{" "}
                  {cfg.account_label}
                </p>
              )}

              {cfg?.webhook_url && (
                <WebhookPanel
                  cfg={cfg}
                  onCopy={() =>
                    navigator.clipboard.writeText(cfg.webhook_url || "")
                  }
                  onReveal={() => setSecretOpen(cfg)}
                  onRotate={() => handleRotate(p.provider)}
                  busy={busy === p.provider}
                />
              )}

              <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                {p.supports_oauth && status !== "CONNECTED" && (
                  <Button
                    size="sm"
                    onClick={() => handleConnect(p.provider)}
                    isLoading={busy === p.provider}
                  >
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                    Connect
                  </Button>
                )}
                {status === "CONNECTED" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDisconnect(p.provider)}
                    isLoading={busy === p.provider}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    Disconnect
                  </Button>
                )}
                {p.supports_webhook && !cfg?.has_webhook_secret && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleRotate(p.provider)}
                    isLoading={busy === p.provider}
                  >
                    <Webhook className="h-4 w-4" aria-hidden="true" />
                    Generate webhook secret
                  </Button>
                )}
                {cfg && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEventsOpen(cfg)}
                  >
                    View events
                  </Button>
                )}
                {p.docs_url && (
                  <a
                    href={p.docs_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline ml-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  >
                    Docs
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/*
        Chat-bot agents — Telegram (bring-your-own-token) and Slack
        (OAuth redirect) sit side-by-side outside the OAuth provider
        grid above because each card owns its own connect/disconnect
        state machine. Rendered in a 2-column grid on desktop so the
        two integrations read as peers at the same tier.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TelegramBotCard />
        <SlackCard />
        <TeamsCard />
        <TwilioSmsCard />
        <QuickBooksCard />
      </div>

      <CareersEmbedCard />

      <SlackConfigCard
        cfg={byProvider.get("SLACK")}
        onSave={async (channel) => {
          try {
            await updateIntegration("SLACK", {
              config: { default_channel: channel },
            });
            addToast("success", "Slack channel updated");
            await load();
          } catch {
            addToast("error", "Could not update Slack channel");
          }
        }}
      />

      {secretOpen && (
        <WebhookSecretModal
          cfg={secretOpen}
          onClose={() => setSecretOpen(null)}
        />
      )}
      {eventsOpen && (
        <EventsModal
          cfg={eventsOpen}
          onClose={() => setEventsOpen(null)}
        />
      )}
    </div>
  );
}


function WebhookPanel({
  cfg,
  onCopy,
  onReveal,
  onRotate,
  busy,
}: {
  cfg: IntegrationRead;
  onCopy: () => void;
  onReveal: () => void;
  onRotate: () => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs space-y-2">
      <div className="flex items-center gap-2 text-text-muted">
        <Webhook className="h-3 w-3" aria-hidden="true" />
        <span className="font-medium">Webhook endpoint</span>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-surface px-2 py-1 text-text-primary">
          {cfg.webhook_url}
        </code>
        <Button size="sm" variant="ghost" onClick={onCopy} aria-label="Copy webhook URL">
          <Copy className="h-3 w-3" aria-hidden="true" />
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" onClick={onReveal}>
          Show signing secret
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRotate}
          isLoading={busy}
          aria-label="Rotate signing secret"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          Rotate
        </Button>
      </div>
    </div>
  );
}


function WebhookSecretModal({
  cfg,
  onClose,
}: {
  cfg: IntegrationRead;
  onClose: () => void;
}) {
  const { addToast } = useToast();
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    revealWebhookSecret(cfg.provider)
      .then((r) => setSecret(r.webhook_secret))
      .catch(() => addToast("error", "Could not fetch secret"))
      .finally(() => setLoading(false));
  }, [cfg.provider, addToast]);

  return (
    <Modal isOpen onClose={onClose} title="Webhook signing secret" size="lg">
      <p className="text-sm text-text-muted mb-4">
        Configure your provider to sign outgoing requests with this secret.
        Anyone with the secret can post events into your org — treat it like
        an API key and rotate if exposed.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading secret…
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-background border border-border px-3 py-2 font-mono text-sm text-text-primary">
            {secret}
          </code>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (secret) {
                navigator.clipboard.writeText(secret);
                addToast("success", "Copied");
              }
            }}
            aria-label="Copy signing secret"
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            Copy
          </Button>
        </div>
      )}
      <div className="mt-4 text-xs text-text-muted">
        <p>
          <span className="text-text-primary">Endpoint:</span>{" "}
          <code>{cfg.webhook_url}</code>
        </p>
        <p className="mt-1">
          Sign each request body with HMAC-SHA256 and send the hex digest in
          the <code>X-Signature-256</code> header (or the provider-specific
          header we accept: X-Slack-Signature, X-Checkr-Signature, X-Hub-Signature-256).
        </p>
      </div>
    </Modal>
  );
}


function EventsModal({
  cfg,
  onClose,
}: {
  cfg: IntegrationRead;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<IntegrationEvent[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listIntegrationEvents(cfg.provider, 50)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [cfg.provider]);

  return (
    <Modal isOpen onClose={onClose} title={`${cfg.provider} — recent events`} size="xl">
      {loading ? (
        <p className="text-sm text-text-muted flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </p>
      ) : events && events.length > 0 ? (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {events.map((e) => (
            <div
              key={e.id}
              className="rounded-lg border border-border bg-background p-3 text-xs"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-text-primary">
                  {e.direction === "inbound" ? "↓" : "↑"} {e.event_type}
                </span>
                <span className="text-[10px] text-text-muted">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
              {e.error ? (
                <p className="text-danger">{e.error}</p>
              ) : (
                <pre className="text-text-muted whitespace-pre-wrap break-all">
                  {JSON.stringify(e.payload, null, 2).slice(0, 500)}
                </pre>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-text-muted">
          No events yet. Connect the integration and trigger a sync to see
          activity here.
        </p>
      )}
    </Modal>
  );
}


function SlackConfigCard({
  cfg,
  onSave,
}: {
  cfg: IntegrationRead | undefined;
  onSave: (channel: string) => Promise<void>;
}) {
  const current = (cfg?.config as Record<string, string> | undefined)
    ?.default_channel as string | undefined;
  const [channel, setChannel] = useState(current || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChannel(current || "");
  }, [current]);

  if (!cfg || cfg.status !== "CONNECTED") return null;

  const dirty = channel.trim() !== (current || "");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Slack — posting channel</CardTitle>
      </CardHeader>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
        <Input
          label="Default channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          placeholder="#peopleops"
          hint="We post leave-approval and org announcements here. Must start with # or be a channel ID."
        />
        <Button
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(channel.trim());
            } finally {
              setSaving(false);
            }
          }}
          isLoading={saving}
          disabled={!dirty || !channel.trim()}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          Save
        </Button>
      </div>
    </Card>
  );
}
